import { createMemo, JSX } from 'solid-js';
import { t } from '@trimble/i18n';
import { lineThicknessOptions } from '@trimble/web3d-providers';
import {
  StrokeOptionDropdown,
  type StrokeOptionDropdownOption,
  type StrokeOptionDropdownOptionGroup,
} from '@trimble/ui-components/solidjs';

/**
 * Preview height (px) for the dropdown line sample. Thickness ≤4 maps 1:1; larger values scale down
 * so the preview stays readable (e.g. 8→5, 12→6, 16→7).
 */
function getLinePreviewHeightPx(thicknessPx: number): number {
  /* eslint-disable no-magic-numbers -- scaling threshold and divisor for preview height */
  return thicknessPx <= 4 ? thicknessPx : 4 + (thicknessPx - 4) / 4;
  /* eslint-enable no-magic-numbers */
}

interface LineThicknessDropdownProps {
  value: number;
  onValueChange(value: number): void;
  disabled?: boolean;
  disabledReason?: string;
}

export const LineThicknessDropdown = (
  props: LineThicknessDropdownProps
): JSX.Element => {
  const options = createMemo((): StrokeOptionDropdownOptionGroup<number>[] => [
    {
      children: lineThicknessOptions.map(
        (valueInPixels): StrokeOptionDropdownOption<number> => {
          const lineHeightPx = getLinePreviewHeightPx(valueInPixels);
          return {
            value: valueInPixels,
            label: `${valueInPixels}px`,
            testId: `line-thickness-option-${valueInPixels}`,
            renderPreview: () => (
              <span
                class="block w-full max-w-10 bg-black"
                style={{ height: `${lineHeightPx}px` }}
              />
            ),
          };
        }
      ),
    },
  ]);

  return (
    <StrokeOptionDropdown<number>
      ariaLabel={t('measurements.measurementStyle.weight')}
      value={props.value}
      onValueChange={props.onValueChange}
      options={options()}
      disabled={props.disabled}
      disabledReason={props.disabledReason}
      triggerTestId="measurement-line-thickness-select"
      listTestId="line-thickness-dropdown-list"
    />
  );
};
